import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import {
  listSubscriptions,
  type Subscription,
  type SubscriptionStatus,
} from '@/lib/api/subscriptions.js';
import { NewSubscriptionSlideOver } from '@/components/subscriptions/NewSubscriptionSlideOver.js';
import { CollectSubscriptionPaymentModal } from '@/components/subscriptions/CollectSubscriptionPaymentModal.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import { cn } from '@/lib/utils.js';

type FilterTab = 'all' | SubscriptionStatus | 'due_today';

const STATUS_TABS: Array<{ id: FilterTab; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'due_today', label: 'Due Today' },
  { id: 'past_due', label: 'Past Due' },
  { id: 'paused', label: 'Paused' },
  { id: 'cancelled', label: 'Cancelled' },
];

function statusBadgeClass(status: string) {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800';
    case 'paused':
      return 'bg-amber-100 text-amber-800';
    case 'past_due':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function SummaryCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-slate-950">{value}</p>
    </div>
  );
}

export function SubscriptionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<FilterTab>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [collectSub, setCollectSub] = useState<Subscription | null>(null);

  const listParams = useMemo(() => {
    if (tab === 'due_today') return { dueToday: true, page: 1, pageSize: 100 };
    if (tab === 'past_due') return { pastDue: true, page: 1, pageSize: 100 };
    if (tab === 'all') return { page: 1, pageSize: 100 };
    return { status: tab, page: 1, pageSize: 100 };
  }, [tab]);

  const subsQuery = useQuery({
    queryKey: ['subscriptions', listParams],
    queryFn: () => listSubscriptions(listParams),
  });

  const summary = subsQuery.data?.meta?.summary;
  const subscriptions = subsQuery.data?.data ?? [];

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">Subscriptions</h1>
          <p className="text-sm text-slate-500">Manage recurring customer plans and billing</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => void subsQuery.refetch()}
          >
            <RefreshCw className={cn('h-4 w-4', subsQuery.isFetching && 'animate-spin')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477]"
          >
            <Plus className="h-4 w-4" />
            New Subscription
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Active" value={summary?.active ?? '—'} />
        <SummaryCard label="MRR" value={summary ? formatCurrency(summary.mrr) : '—'} />
        <SummaryCard label="Due Today" value={summary?.dueToday ?? '—'} />
        <SummaryCard label="Past Due" value={summary?.pastDue ?? '—'} />
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-semibold transition',
              tab === t.id
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Customer</th>
              <th className="px-5 py-3">Plan</th>
              <th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">Frequency</th>
              <th className="px-5 py-3">Next Billing</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {subscriptions.map((sub) => (
              <tr key={sub.id} className="hover:bg-slate-50">
                <td className="px-5 py-4">
                  <button
                    type="button"
                    className="font-semibold text-primary hover:underline"
                    onClick={() => navigate(`/customers/${sub.customerId}`)}
                  >
                    {sub.customerName ?? 'Customer'}
                  </button>
                  {sub.customerPhone ? (
                    <p className="text-xs text-slate-500">{sub.customerPhone}</p>
                  ) : null}
                </td>
                <td className="px-5 py-4 font-medium text-slate-950">{sub.name}</td>
                <td className="px-5 py-4 text-slate-600">{formatCurrency(sub.amount)}</td>
                <td className="px-5 py-4 capitalize text-slate-600">{prettyLabel(sub.frequency)}</td>
                <td className="px-5 py-4 text-slate-600">{formatDate(sub.nextBillingDate)}</td>
                <td className="px-5 py-4">
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-xs font-bold uppercase',
                      statusBadgeClass(sub.status),
                    )}
                  >
                    {prettyLabel(sub.status)}
                  </span>
                </td>
                <td className="px-5 py-4 text-right">
                  {sub.status === 'active' || sub.status === 'past_due' ? (
                    <button
                      type="button"
                      className="text-sm font-semibold text-primary hover:underline"
                      onClick={() => setCollectSub(sub)}
                    >
                      Collect Payment
                    </button>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!subscriptions.length && !subsQuery.isLoading ? (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-slate-500">
                  No subscriptions found.
                </td>
              </tr>
            ) : null}
            {subsQuery.isLoading ? (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-slate-500">
                  Loading subscriptions…
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <NewSubscriptionSlideOver
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />

      <CollectSubscriptionPaymentModal
        open={Boolean(collectSub)}
        subscription={collectSub}
        onClose={() => setCollectSub(null)}
        onSuccess={refresh}
      />
    </div>
  );
}
